import React, { useState } from 'react'
import styled from 'styled-components'

const Section = styled.div`
  margin-top: 2rem;
`

const Title = styled.h2`
  font-size: 1.5rem;
  color: var(--color-primary);
  margin-bottom: 1rem;
`

const LanguageTabs = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`

const LanguageTab = styled.button`
  background: none;
  border: none;
  color: ${props => props.$active ? 'var(--color-primary)' : 'var(--color-text-secondary)'};
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 1rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => props.$active ? 'var(--color-primary)' : 'transparent'};
  }
`

const CodeBlock = styled.pre`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
  overflow-x: auto;
  font-family: 'Fira Code', monospace;
  font-size: 0.9rem;
  color: #e2e8f0;
`

const InstallBlock = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;

  h3 {
    color: var(--color-text);
    margin-bottom: 0.5rem;
  }
`

const sdkExamples = {
  javascript: {
    install: 'npm install @ai-agents/sdk',
    basic: `import { AIAgentsPlatform } from '@ai-agents/sdk';

// Initialize the client
const client = new AIAgentsPlatform({
  apiKey: 'your_api_key',
});

// Create a new project
const project = await client.projects.create({
  name: 'Website Development',
  description: 'Create a modern React website',
  agents: {
    coders: {
      count: 3,
      capabilities: ['frontend', 'react']
    }
  }
});

// Stream updates
const unsubscribe = project.onUpdate((update) => {
  console.log('Project update:', update);
});

// Deploy agents
await project.deployAgents();

// Get project outputs
const outputs = await project.getOutputs();
console.log('Project outputs:', outputs);`,
    advanced: `// Advanced usage with webhooks
const webhook = await client.webhooks.create({
  url: 'https://your-domain.com/webhook',
  events: ['project.completed', 'agent.deployed'],
  metadata: {
    environment: 'production'
  }
});

// Custom error handling
try {
  const result = await client.agents.deploy({
    project_id: 'proj_123',
    config: {
      timeout: 3600,
      retries: 3,
      errorHandling: {
        onTimeout: 'restart',
        onError: 'notify'
      }
    }
  });
} catch (error) {
  if (error.code === 'RATE_LIMIT_EXCEEDED') {
    await sleep(error.retryAfter);
    // Retry the request
  }
}`
  },
  python: {
    install: 'pip install ai-agents-sdk',
    basic: `from ai_agents_platform import Client, Project, Agent

# Initialize the client
client = Client(api_key="your_api_key")

# Create a new project
project = Project.create(
    name="Website Development",
    description="Create a modern React website",
    agents={
        "coders": {
            "count": 3,
            "capabilities": ["frontend", "react"]
        }
    }
)

# Monitor progress
for event in project.stream_events():
    print(f"Event: {event.type}, Status: {event.status}")

# Deploy agents
agents = project.deploy_agents()

# Get project outputs
outputs = project.get_outputs()
print("Project outputs:", outputs)`,
    advanced: `# Advanced usage with async/await
import asyncio
from ai_agents_platform.async_client import AsyncClient

async def main():
    client = AsyncClient(api_key="your_api_key")
    
    # Create webhook
    webhook = await client.webhooks.create(
        url="https://your-domain.com/webhook",
        events=["project.completed", "agent.deployed"],
        metadata={
            "environment": "production"
        }
    )
    
    # Custom error handling
    try:
        result = await client.agents.deploy(
            project_id="proj_123",
            config={
                "timeout": 3600,
                "retries": 3,
                "error_handling": {
                    "on_timeout": "restart",
                    "on_error": "notify"
                }
            }
        )
    except RateLimitError as error:
        await asyncio.sleep(error.retry_after)
        # Retry the request

asyncio.run(main())`
  },
  rust: {
    install: 'cargo add ai-agents-platform',
    basic: `use ai_agents_platform::{Client, ProjectConfig, AgentConfig};

#[tokio::main]
async fn main() -> Result<(), Box<dyn std::error::Error>> {
    let client = Client::new("your_api_key")?;

    // Create a new project
    let project = client.create_project(ProjectConfig {
        name: "Website Development",
        description: "Create a modern React website",
        agents: vec![
            AgentConfig {
                agent_type: "coder",
                count: 3,
                capabilities: vec!["frontend", "react"],
            }
        ],
    })?;

    // Stream updates
    let mut events = project.subscribe_events()?;
    while let Some(event) = events.next().await {
        println!("Event received: {:?}", event);
    }

    // Deploy agents
    project.deploy_agents().await?;

    // Get project outputs
    let outputs = project.get_outputs().await?;
    println!("Project outputs: {:?}", outputs);

    Ok(())
}`,
    advanced: `use ai_agents_platform::{
    Client, WebhookConfig, AgentDeployConfig,
    error::RateLimitError
};

#[tokio::main]
async fn main() -> Result<(), Box<dyn std::error::Error>> {
    let client = Client::new("your_api_key")?;

    // Create webhook
    let webhook = client.webhooks().create(WebhookConfig {
        url: "https://your-domain.com/webhook",
        events: vec!["project.completed", "agent.deployed"],
        metadata: HashMap::from([
            ("environment", "production")
        ]),
    }).await?;

    // Custom error handling
    match client.agents().deploy(AgentDeployConfig {
        project_id: "proj_123",
        timeout: Duration::from_secs(3600),
        retries: 3,
        error_handling: ErrorHandling {
            on_timeout: ErrorAction::Restart,
            on_error: ErrorAction::Notify,
        },
    }).await {
        Ok(result) => println!("Deploy successful: {:?}", result),
        Err(Error::RateLimit(e)) => {
            tokio::time::sleep(e.retry_after).await;
            // Retry the request
        }
        Err(e) => return Err(e.into()),
    }

    Ok(())
}`
  }
}

export default function SDKSection() {
  const [activeLanguage, setActiveLanguage] = useState('javascript')
  const [showAdvanced, setShowAdvanced] = useState(false)

  return (
    <Section>
      <Title>SDK Documentation</Title>

      <LanguageTabs>
        <LanguageTab 
          $active={activeLanguage === 'javascript'}
          onClick={() => setActiveLanguage('javascript')}
        >
          JavaScript/TypeScript
        </LanguageTab>
        <LanguageTab 
          $active={activeLanguage === 'python'}
          onClick={() => setActiveLanguage('python')}
        >
          Python
        </LanguageTab>
        <LanguageTab 
          $active={activeLanguage === 'rust'}
          onClick={() => setActiveLanguage('rust')}
        >
          Rust
        </LanguageTab>
      </LanguageTabs>

      <InstallBlock>
        <h3>Installation</h3>
        <CodeBlock>
          {sdkExamples[activeLanguage].install}
        </CodeBlock>
      </InstallBlock>

      <div>
        <h3 style={{ marginBottom: '1rem' }}>Basic Usage</h3>
        <CodeBlock>
          {sdkExamples[activeLanguage].basic}
        </CodeBlock>
      </div>

      <div>
        <h3 style={{ 
          marginBottom: '1rem', 
          display: 'flex', 
          alignItems: 'center',
          gap: '1rem'
        }}>
          Advanced Usage
          <button 
            onClick={() => setShowAdvanced(!showAdvanced)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-primary)',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            {showAdvanced ? 'Hide' : 'Show'}
          </button>
        </h3>
        {showAdvanced && (
          <CodeBlock>
            {sdkExamples[activeLanguage].advanced}
          </CodeBlock>
        )}
      </div>
    </Section>
  )
}
